import { useEffect, useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import PageHeader from '../components/PageHeader.jsx';
import ErrorBanner from '../components/ErrorBanner.jsx';
import { faqsAPI, knowledgeAPI } from '../services/api.js';

// Post-login help/FAQ — same content the AI uses to answer calls, so the
// tenant's team and the AI stay aligned. Read-only for now; CRUD lives at
// /faqs and /knowledge endpoints (admin-only) for a later editor.

export default function Help() {
  const [faqs, setFaqs] = useState([]);
  const [knowledge, setKnowledge] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [f, k] = await Promise.all([
          faqsAPI.list().catch((e) => (e.status === 404 ? { data: [] } : Promise.reject(e))),
          knowledgeAPI.list().catch((e) => (e.status === 404 ? { data: [] } : Promise.reject(e)))
        ]);
        if (cancelled) return;
        setFaqs(f?.data || []);
        setKnowledge(k?.data || []);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load help content.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const grouped = useMemo(() => {
    const by = {};
    for (const f of faqs) {
      const c = f.category || 'General';
      (by[c] = by[c] || []).push(f);
    }
    return Object.entries(by);
  }, [faqs]);

  return (
    <div className="px-6 md:px-10 py-8 max-w-4xl">
      <PageHeader
        title="Help"
        subtitle="The same FAQs and knowledge articles your AI uses on calls"
      />

      {error && (
        <div className="mb-6">
          <ErrorBanner message={error} />
        </div>
      )}

      {loading ? (
        <div className="card text-stone-500">Loading…</div>
      ) : (
        <>
          <h2 className="text-lg text-stone-100 mb-3">Frequently asked questions</h2>
          {grouped.length === 0 ? (
            <div className="card text-stone-500 mb-8">
              No FAQs yet. Add some so your AI can answer them on calls.
            </div>
          ) : (
            <div className="space-y-6 mb-10">
              {grouped.map(([cat, list]) => (
                <section key={cat}>
                  <h3 className="text-xs uppercase tracking-[0.25em] text-alfred-gold mb-2">
                    {cat}
                  </h3>
                  <ul className="card p-0 overflow-hidden divide-y divide-stone-900">
                    {list.map((f) => {
                      const isOpen = !!open[f.id];
                      return (
                        <li key={f.id}>
                          <button
                            type="button"
                            onClick={() => setOpen((o) => ({ ...o, [f.id]: !o[f.id] }))}
                            className="w-full flex items-start justify-between gap-4 px-5 py-4 text-left hover:bg-stone-900/50 transition"
                            aria-expanded={isOpen}
                          >
                            <span className="text-stone-100">{f.question}</span>
                            <ChevronDown
                              size={18}
                              className={`shrink-0 mt-0.5 text-stone-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                            />
                          </button>
                          {isOpen && (
                            <div className="px-5 pb-5 text-sm text-stone-300 leading-relaxed whitespace-pre-wrap">
                              {f.answer}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))}
            </div>
          )}

          <h2 className="text-lg text-stone-100 mb-3">Knowledge articles</h2>
          {knowledge.length === 0 ? (
            <div className="card text-stone-500">
              No knowledge articles yet. Add product/policy details so the AI can answer factually.
            </div>
          ) : (
            <ul className="space-y-4">
              {knowledge.map((k) => (
                <li key={k.id} className="card">
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="text-stone-100 font-medium">{k.title}</h3>
                    {k.category && (
                      <span className="badge bg-stone-800 text-stone-300 border border-stone-700 shrink-0">
                        {k.category}
                      </span>
                    )}
                  </div>
                  <p className="mt-2 text-sm text-stone-300 leading-relaxed whitespace-pre-wrap">
                    {k.content}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
